import * as Collapsible from "@radix-ui/react-collapsible";
import { ChevronUp as ChevronUp20 } from "@alchemy/ui/icons/20";
import { ChevronUp as ChevronUp16 } from "@alchemy/ui/icons/16";
import { motion } from "framer-motion";
import { useCallback } from "react";
import classNames from "classnames";

import useLDFeatureFlag from "@features/launchdarkly/useLDFeatureFlag";
import { FeatureFlagKey } from "@features/launchdarkly/featureFlag";
import { NavItemGroup } from "./use-nav-items";

interface NavButtonGroupProps {
  group: NavItemGroup;
  children: React.ReactNode;
  expanded: boolean;
  onToggle: (group: NavItemGroup) => void;
}

const NavButtonGroup = ({
  group,
  children,
  expanded,
  onToggle,
}: NavButtonGroupProps) => {
  const { data: enableNavRedesign } = useLDFeatureFlag(
    FeatureFlagKey.NavRedesign,
  );

  const handleToggle = useCallback(() => {
    onToggle(group);
  }, [group, onToggle]);

  const IconComponent = enableNavRedesign ? ChevronUp16 : ChevronUp20;

  return (
    <Collapsible.Root asChild open={expanded} onOpenChange={handleToggle}>
      <li>
        <Collapsible.Trigger
          className={classNames(
            "group flex w-full items-center rounded text-grayscale-400 outline-none transition",
            "focus-visible:shadow-focus-dark",
            enableNavRedesign
              ? "mb-0.5 mt-3 px-2 py-0.5 text-paragraph-100-regular"
              : "mb-1 mt-6 pl-2 pr-0.5 text-label-100 ",
          )}
        >
          {group.title}

          <IconComponent className="ml-auto text-grayscale-500 transition group-hover:rotate-180 group-aria-expanded:rotate-180 group-aria-expanded:group-hover:rotate-0" />
        </Collapsible.Trigger>

        <Collapsible.Content asChild forceMount>
          <motion.ul
            transition={{ duration: 0.15 }}
            initial={false}
            className="-mx-1 overflow-hidden px-1"
            animate={
              expanded
                ? {
                    opacity: 1,
                    height: "auto",
                    visibility: "visible",
                  }
                : {
                    opacity: 0,
                    height: 0,
                    transitionEnd: { visibility: "hidden" },
                  }
            }
          >
            {children}
          </motion.ul>
        </Collapsible.Content>
      </li>
    </Collapsible.Root>
  );
};

export default NavButtonGroup;
